import { useState, useRef, useCallback } from 'react';

import { AUDIO_CONSTRAINTS, MEDIA_RECORDER_OPTIONS } from '../constants/audio';
import { createRecordingError } from '../types';

export interface UseMediaRecorderProps {
  onRecordingComplete?: (audioBlob: Blob) => void;
  onRecordingStart?: () => void;
  onRecordingStop?: () => void;
  onError?: (error: Error) => void;
}

export interface UseMediaRecorderReturn {
  isRecording: boolean;
  startRecording: () => Promise<void>;
  stopRecording: () => void;
  error: Error | null;
}

export const useMediaRecorder = (
  props: UseMediaRecorderProps,
): UseMediaRecorderReturn => {
  const [isRecording, setIsRecording] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  const cleanup = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    mediaRecorderRef.current = null;
    setIsRecording(false);
    setError(null);
  }, []);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      props.onRecordingStop?.();
    }
  }, [isRecording, props.onRecordingStop]);

  const setupMediaRecorder = useCallback(
    (stream: MediaStream) => {
      const mediaRecorder = new MediaRecorder(stream, MEDIA_RECORDER_OPTIONS);

      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = event => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(chunksRef.current, { type: 'audio/webm' });
        props.onRecordingComplete?.(audioBlob);
        cleanup();
      };

      return mediaRecorder;
    },
    [cleanup, props.onRecordingComplete],
  );

  const startRecording = useCallback(async () => {
    try {
      setError(null);

      if (!navigator.mediaDevices?.getUserMedia) {
        throw createRecordingError(
          'Audio recording is not supported in this browser',
          'not-supported',
        );
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: AUDIO_CONSTRAINTS,
      });

      streamRef.current = stream;
      const mediaRecorder = setupMediaRecorder(stream);

      mediaRecorder.start();
      setIsRecording(true);
      props.onRecordingStart?.();
    } catch (err) {
      let error;

      if (err instanceof Error) {
        if (err.name === 'NotAllowedError') {
          error = createRecordingError(
            'Microphone permission denied. Please allow microphone access.',
            'permission',
            err.name,
          );
        } else if (err.name === 'NotFoundError') {
          error = createRecordingError(
            'No microphone found. Please connect a microphone.',
            'not-supported',
            err.name,
          );
        } else {
          error = createRecordingError(err.message, 'unknown', err.name);
        }
      } else {
        error = createRecordingError('Recording failed', 'unknown');
      }

      setError(error);
      props.onError?.(error);
      cleanup();
    }
  }, [cleanup, props.onError, props.onRecordingStart, setupMediaRecorder]);

  return {
    isRecording,
    startRecording,
    stopRecording,
    error,
  };
};
